import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import {
  ArrowLeft, ArrowUpRight, Star, Award, BookOpen, Gamepad2,
  Palette, Printer, Play, Heart, Shield, CloudRain, Flame,
  Dog, Cat, Bird, Rabbit, Fish, HelpCircle, Sparkles, Trophy,
  PartyPopper, Smile, Music, PawPrint, Search,
} from "lucide-react";

const B = {
  border: "4px solid #29294d",
  shadow: "8px 8px 0px #29294d",
  shadowSm: "5px 5px 0px #29294d",
  font: "'Space Grotesk', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

// Fun colors for kids
const PINK = "#ff6b9d";
const YELLOW = "#ffd93d";
const GREEN = "#6bcb77";
const BLUE = "#4d96ff";
const PURPLE = "#9b5de5";
const ORANGE = "#ff9f43";
const NAVY = "#29294d";
const CREAM = "#fff9e6";

function KidsButton({ children, bg = PINK, color = "#fff", onClick, className = "" }) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 px-6 py-4 font-bold uppercase tracking-wider text-base transition-all duration-200 hover:-translate-y-1 hover:scale-105 ${className}`}
      style={{ fontFamily: B.font, background: bg, color, border: B.border, boxShadow: B.shadowSm, borderRadius: "12px" }}
    >{children}</button>
  );
}

function KidsBadge({ children, bg = YELLOW, color = NAVY, rotate = "-3deg" }) {
  return (
    <span className="inline-block px-4 py-2 text-sm font-bold uppercase tracking-wider"
      style={{ fontFamily: B.font, background: bg, color, border: B.border, transform: `rotate(${rotate})`, boxShadow: B.shadowSm, borderRadius: "8px" }}
    >{children}</span>
  );
}

function KidsCard({ children, className = "", bg = "#fff", hoverBg, style = {} }) {
  return (
    <div className={`transition-all duration-200 hover:-translate-y-2 hover:scale-[1.02] cursor-pointer ${className}`}
      style={{ background: bg, border: B.border, boxShadow: B.shadow, borderRadius: "16px", ...style }}
    >{children}</div>
  );
}

const AGE_GROUPS = [
  {
    id: "pequeños",
    age: "3-6",
    title: "PEQUEÑOS HÉROES",
    icon: Star,
    color: PINK,
    desc: "Cuentos, canciones y dibujos para colorear",
    activities: ["Cuentos ilustrados", "Colorear", "Canciones", "Juegos simples"],
  },
  {
    id: "exploradores",
    age: "6-10",
    title: "EXPLORADORES",
    icon: Search,
    color: BLUE,
    desc: "Juegos, quiz y actividades interactivas",
    activities: ["Quiz divertidos", "Juegos", "Vídeos", "Manualidades"],
  },
  {
    id: "guardianes",
    age: "10-14",
    title: "GUARDIANES",
    icon: Shield,
    color: PURPLE,
    desc: "Retos, certificados y proyectos",
    activities: ["Retos", "Proyectos", "Certificados", "Liderazgo"],
  },
];

const CONTENT_TYPES = [
  { icon: BookOpen, title: "CUENTOS", desc: "Historias de animales valientes", color: PINK },
  { icon: Gamepad2, title: "JUEGOS", desc: "Aprende jugando", color: BLUE },
  { icon: Play, title: "VÍDEOS", desc: "Aprende viendo", color: GREEN },
  { icon: Palette, title: "COLOREAR", desc: "Descarga y pinta", color: YELLOW },
  { icon: Printer, title: "ACTIVIDADES", desc: "Imprime y aprende", color: ORANGE },
  { icon: Award, title: "QUIZ", desc: "¡Demuestra lo que sabes!", color: PURPLE },
];

const TOPICS = [
  { icon: CloudRain, title: "Tormentas y lluvia", color: BLUE },
  { icon: Flame, title: "Incendios", color: ORANGE },
  { icon: Shield, title: "Proteger a mi animal", color: GREEN },
  { icon: Heart, title: "Primeros auxilios", color: PINK },
  { icon: HelpCircle, title: "Animales perdidos", color: PURPLE },
  { icon: PawPrint, title: "La mochila de emergencia", color: YELLOW },
];

const REWARDS = [
  { icon: Star, title: "Estrellas", desc: "Gana estrellas en cada actividad", color: YELLOW },
  { icon: Award, title: "Badges", desc: "Colecciona insignias especiales", color: PURPLE },
  { icon: Trophy, title: "Trofeos", desc: "Completa retos para ganar trofeos", color: ORANGE },
  { icon: Sparkles, title: "Certificados", desc: "¡Certifícate como Guardián Animal!", color: PINK },
];

const ANIMALS = [
  { icon: Dog, name: "Perros" },
  { icon: Cat, name: "Gatos" },
  { icon: Bird, name: "Pájaros" },
  { icon: Rabbit, name: "Conejos" },
  { icon: Fish, name: "Peces" },
];

export default function AprendeKidsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedAge, setSelectedAge] = useState(null);

  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: B.font }}>
      {/* Header */}
      <header className="sticky top-0 z-50 px-6 py-4" style={{ background: CREAM, borderBottom: B.border }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/aprende")} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ fontFamily: B.mono, color: NAVY }}>
              <ArrowLeft className="w-4 h-4" /> APRENDE
            </button>
            <div style={{ width: "2px", height: "28px", background: NAVY, borderRadius: "2px" }} />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center" style={{ background: YELLOW, border: B.border, borderRadius: "12px" }}>
                <PawPrint className="w-6 h-6" style={{ color: NAVY }} />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight" style={{ color: NAVY }}>APRENDE KIDS</h1>
                <p className="text-xs tracking-wider" style={{ fontFamily: B.mono, color: PINK }}>¡PEQUEÑOS HÉROES ANIMALES!</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 px-4 py-2" style={{ background: YELLOW, border: B.border, borderRadius: "8px" }}>
              <Star className="w-5 h-5" style={{ color: ORANGE }} fill={ORANGE} />
              <span className="font-bold" style={{ color: NAVY }}>0 estrellas</span>
            </div>
            <KidsButton bg={NAVY} color={CREAM} onClick={() => navigate(user ? "/aprende/panel" : "/auth")}>
              {user ? "MI PERFIL" : "ENTRAR"} <ArrowUpRight className="w-5 h-5" />
            </KidsButton>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-12 pb-16 overflow-hidden" style={{ background: `linear-gradient(180deg, ${CREAM} 0%, #fff 100%)` }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex justify-center gap-3 mb-6">
              <KidsBadge bg={PINK} rotate="-3deg">¡NUEVO!</KidsBadge>
              <KidsBadge bg={YELLOW} rotate="2deg">PARA NIÑOS</KidsBadge>
              <KidsBadge bg={GREEN} rotate="-1deg">¡GRATIS!</KidsBadge>
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold leading-tight mb-6" style={{ color: NAVY }}>
              ¡APRENDE A <span style={{ color: PINK }}>PROTEGER</span>
              <br />
              A LOS <span style={{ color: BLUE }}>ANIMALES</span>!
            </h1>
            <p className="text-xl max-w-2xl mx-auto mb-8" style={{ color: NAVY, opacity: 0.8 }}>
              Juegos, cuentos y actividades para convertirte en 
              <strong style={{ color: PURPLE }}> superhéroe o superheroína de los animales</strong>
            </p>
            
            {/* Floating animals */}
            <div className="flex justify-center gap-4 mb-8 animate-bounce">
              {[Dog, Cat, Rabbit, Bird, Fish].map((AnimalIcon, i) => (
                <AnimalIcon key={i} className="w-9 h-9" style={{ color: NAVY, animationDelay: `${i * 0.1}s` }} />
              ))}
            </div>

            <KidsButton bg={PINK} className="text-lg px-8 py-5" onClick={() => navigate("/aprende/kids/cuento/lluvia")}>
              ¡EMPEZAR A JUGAR! <Gamepad2 className="w-6 h-6" />
            </KidsButton>
          </div>
        </div>
      </section>

      {/* Featured Story Banner */}
      <section className="px-6 py-6" style={{ background: BLUE }}>
        <div className="max-w-7xl mx-auto">
          <KidsCard className="p-6 flex flex-col md:flex-row items-center gap-6" bg="#fff" onClick={() => navigate("/aprende/kids/cuento/lluvia")}>
            <div className="w-24 h-24 flex items-center justify-center flex-shrink-0" style={{ background: PINK, border: B.border, borderRadius: "50%" }}>
              <Dog className="w-12 h-12" style={{ color: "#fff" }} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <KidsBadge bg={YELLOW} rotate="-2deg">¡NUEVO CUENTO!</KidsBadge>
              <h3 className="text-2xl font-bold mt-3 mb-2" style={{ color: NAVY }}>El día que llovió mucho</h3>
              <p className="text-base" style={{ color: "#666" }}>Acompaña a Lucía y su perrita Canela en una aventura durante una tormenta. ¡Aprende cómo cuidar de los animales cuando tienen miedo!</p>
            </div>
            <KidsButton bg={PINK} className="flex-shrink-0">
              ¡LEER AHORA! <BookOpen className="w-5 h-5" />
            </KidsButton>
          </KidsCard>
        </div>
      </section>

      {/* Age Groups */}
      <section className="px-6 py-16" style={{ background: "#fff" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: NAVY }}>
              ¿CUÁNTOS AÑOS TIENES?
            </h2>
            <p className="text-lg" style={{ color: NAVY, opacity: 0.7 }}>
              Elige tu grupo y descubre contenido especial para ti
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {AGE_GROUPS.map((group) => (
              <KidsCard
                key={group.id}
                className="p-6 text-center"
                bg={selectedAge === group.id ? group.color : "#fff"}
                onClick={() => setSelectedAge(group.id)}
              >
                <group.icon className="w-14 h-14 mx-auto mb-4" style={{ color: selectedAge === group.id ? "#fff" : group.color }} />
                <div className="inline-block px-4 py-2 mb-4 font-bold text-2xl" style={{ background: group.color, color: "#fff", border: B.border, borderRadius: "8px" }}>
                  {group.age} AÑOS
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: selectedAge === group.id ? "#fff" : NAVY }}>{group.title}</h3>
                <p className="text-sm mb-4" style={{ color: selectedAge === group.id ? "rgba(255,255,255,0.9)" : "#666" }}>{group.desc}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {group.activities.map((act, i) => (
                    <span key={i} className="px-2 py-1 text-xs font-bold" style={{ 
                      background: selectedAge === group.id ? "rgba(255,255,255,0.3)" : group.color + "20",
                      color: selectedAge === group.id ? "#fff" : group.color,
                      borderRadius: "4px"
                    }}>
                      {act}
                    </span>
                  ))}
                </div>
              </KidsCard>
            ))}
          </div>
        </div>
      </section>

      {/* Content Types */}
      <section className="px-6 py-16" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: NAVY }}>
              ¿QUÉ QUIERES HACER?
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CONTENT_TYPES.map((content, i) => (
              <KidsCard key={i} className="p-4 text-center" bg="#fff">
                <content.icon className="w-9 h-9 mx-auto mb-3" style={{ color: content.color }} />
                <h3 className="text-sm font-bold mb-1" style={{ color: NAVY }}>{content.title}</h3>
                <p className="text-xs" style={{ color: "#666" }}>{content.desc}</p>
              </KidsCard>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="px-6 py-16" style={{ background: "#fff" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: NAVY }}>
              APRENDE SOBRE...
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {TOPICS.map((topic, i) => (
              <KidsCard key={i} className="p-5 flex items-center gap-4" bg="#fff">
                <div className="w-14 h-14 flex items-center justify-center flex-shrink-0" style={{ background: topic.color, border: B.border, borderRadius: "12px" }}>
                  <topic.icon className="w-7 h-7" style={{ color: "#fff" }} />
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: NAVY }}>{topic.title}</h3>
                </div>
              </KidsCard>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section className="px-6 py-16" style={{ background: PURPLE }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: "#fff" }}>
              ¡GANA PREMIOS! <Trophy className="w-9 h-9 inline" style={{ color: YELLOW }} />
            </h2>
            <p className="text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
              Completa actividades y consigue recompensas increíbles
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {REWARDS.map((reward, i) => (
              <div key={i} className="text-center p-6" style={{ background: "rgba(255,255,255,0.15)", borderRadius: "16px", border: "3px solid rgba(255,255,255,0.3)" }}>
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ background: reward.color, border: B.border, borderRadius: "50%" }}>
                  <reward.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: "#fff" }}>{reward.title}</h3>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>{reward.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animals we help */}
      <section className="px-6 py-16" style={{ background: CREAM }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" style={{ color: NAVY }}>
            ANIMALES QUE PROTEGEMOS
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {ANIMALS.map((animal, i) => (
              <div key={i} className="flex flex-col items-center p-4" style={{ background: "#fff", border: B.border, borderRadius: "16px", boxShadow: B.shadowSm }}>
                <animal.icon className="w-10 h-10 mb-2" style={{ color: NAVY }} />
                <span className="font-bold text-sm" style={{ color: NAVY }}>{animal.name}</span>
              </div>
            ))}
            <div className="flex flex-col items-center p-4" style={{ background: PINK, border: B.border, borderRadius: "16px", boxShadow: B.shadowSm }}>
              <PawPrint className="w-10 h-10 mb-2" style={{ color: "#fff" }} />
              <span className="font-bold text-sm" style={{ color: "#fff" }}>¡Y más!</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-16" style={{ background: YELLOW }}>
        <div className="max-w-4xl mx-auto text-center">
          <PartyPopper className="w-14 h-14 mx-auto mb-6" style={{ color: NAVY }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: NAVY }}>
            ¿LISTO PARA SER HÉROE O HEROÍNA?
          </h2>
          <p className="text-lg mb-8" style={{ color: NAVY, opacity: 0.8 }}>
            ¡Únete a miles de niños y niñas que ya están aprendiendo a proteger a los animales!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <KidsButton bg={PINK} className="text-lg">
              <PartyPopper className="w-6 h-6" /> ¡EMPEZAR AHORA!
            </KidsButton>
            <KidsButton bg={NAVY} color={CREAM} onClick={() => navigate("/aprende")} className="text-lg">
              <ArrowLeft className="w-6 h-6" /> VOLVER A APRENDE
            </KidsButton>
          </div>
        </div>
      </section>
    </div>
  );
}
